(() => {
  'use strict';

  // Global Dawn cart guard: keep the welcome bottle only while at least
  // one subscription line is in the cart. Do not depend on Appstle plan IDs.
  if (window.__subscriptionGiftCartGuard) return;
  window.__subscriptionGiftCartGuard = true;

  const GIFT_OFFER = 'hydrate-upsell';
  const GIFT_ROLE = 'free-bottle';
  const PAID_ROLE = 'paid-bottle';
  const routesRoot = window.Shopify?.routes?.root || '/';
  const cartEndpoint = (suffix) => `${routesRoot.replace(/\/?$/, '/')}${suffix === 'cart.js' ? 'cart.js' : `cart/${suffix}`}`;

  let auditTimer = null;
  let auditing = false;
  let needsAnotherAudit = false;
  let removalPending = false;
  let failures = 0;

  const isCartMutation = (input, init) => {
    const raw = typeof input === 'string' ? input : input?.url;
    if (!raw) return false;
    try {
      const url = new URL(raw, window.location.href);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      return (
        url.origin === window.location.origin &&
        method !== 'GET' &&
        /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname)
      );
    } catch (_) {
      return false;
    }
  };

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const mutation = isCartMutation(args[0], args[1]);
    const promise = originalFetch.apply(this, args);
    if (mutation) {
      Promise.resolve(promise).then(
        (response) => {
          if (response.ok) {
            removalPending = false;
            scheduleAudit(180);
          }
        },
        () => {
          removalPending = false;
        }
      );
    }
    return promise;
  };

  function scheduleAudit(delay = 80) {
    if (auditing) {
      needsAnotherAudit = true;
      return;
    }
    window.clearTimeout(auditTimer);
    auditTimer = window.setTimeout(audit, delay);
  }

  const isFreeBottle = (item) =>
    item?.properties?._khu_offer === GIFT_OFFER &&
    item?.properties?._khu_role === GIFT_ROLE;

  const hasSubscription = (cart) =>
    (cart?.items || []).some(
      (item) => item.quantity > 0 && Boolean(item.selling_plan_allocation)
    );

  async function requestCart() {
    const response = await window.fetch(cartEndpoint('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  }

  async function postJson(url, payload) {
    const response = await window.fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    return response.json();
  }

  function findDawnCartComponent() {
    const candidates = window.location.pathname.replace(/\/$/, '').endsWith('/cart')
      ? ['cart-items', 'cart-drawer-items']
      : ['cart-drawer-items', 'cart-items'];
    for (const selector of candidates) {
      const component = document.querySelector(selector);
      if (typeof component?.updateQuantity === 'function') return component;
    }
    return null;
  }

  async function restorePaidExtras(gift) {
    // In the existing Hydrate upsell, one *marked* gift line can contain
    // several bottles: one is free, the others were intended to be paid.
    // Preserve the extras as a separate non-gift line before removing the gift.
    if (gift.quantity <= 1) return;
    const variantId = Number(gift.variant_id || gift.id);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) {
      throw new Error('Invalid bottle variant ID');
    }
    const properties = {
      ...(gift.properties || {}),
      _khu_role: PAID_ROLE,
      _khu_purchase_type: 'one-time',
    };
    await postJson(cartEndpoint('add.js'), {
      items: [{ id: variantId, quantity: gift.quantity - 1, properties }],
    });
  }

  async function removeGiftLine(lineIndex, gift) {
    const component = findDawnCartComponent();
    if (component) {
      // Delegate to Dawn so its drawer/page and cart bubble update natively.
      removalPending = true;
      component.updateQuantity(lineIndex, 0, 'subscription-gift-cleanup', gift.variant_id);
      // A failed request should never leave the guard permanently pending.
      window.setTimeout(() => {
        if (removalPending) {
          removalPending = false;
          scheduleAudit(250);
        }
      }, 3500);
      return;
    }

    // Defensive fallback for a theme without Dawn's cart custom elements.
    await postJson(cartEndpoint('change.js'), { line: lineIndex, quantity: 0 });
    window.location.reload();
  }

  async function audit() {
    auditTimer = null;
    if (auditing || removalPending) {
      needsAnotherAudit = true;
      return;
    }
    auditing = true;
    try {
      const cart = await requestCart();
      if (hasSubscription(cart)) {
        failures = 0;
        return;
      }
      const giftIndex = (cart.items || []).findIndex(isFreeBottle);
      if (giftIndex < 0) {
        failures = 0;
        return;
      }

      const gift = cart.items[giftIndex];
      // Avoid dropping separately paid extras when the upsell quantity was >1.
      await restorePaidExtras(gift);
      await removeGiftLine(giftIndex + 1, gift);
      failures = 0;
    } catch (error) {
      failures += 1;
      console.error('[Subscription gift guard] Cart cleanup failed:', error);
      if (failures <= 2) window.setTimeout(() => scheduleAudit(100), 1200);
    } finally {
      auditing = false;
      if (needsAnotherAudit && !removalPending) {
        needsAnotherAudit = false;
        scheduleAudit(180);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleAudit(100), { once: true });
  } else {
    scheduleAudit(100);
  }
  window.addEventListener('pageshow', () => scheduleAudit(160));
  document.addEventListener('shopify:section:load', () => scheduleAudit(160));
})();
